// System dependencies

import { createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

// Application dependencies

import { AuthorisationProduct } from '@/interfaces/authorisation/authorisation.interfaces';
import { addUserByEmailAction, getUserByEmailAction } from '@/actions/authorisation/user.actions';
import { authorisationLoadAuthorisation } from '@/redux/authorisation/authorisation.slices';
import { formatErrorMessage } from '@/lib/functions';
import { getProductByNameAction } from '@/actions/authorisation/products.actions';
import { userLoadNonPortalProduct, userLoadPortalProduct, userLoadUser } from '@/redux/user/user.slices';

/**
 * Fetches a user's data by email address and updates the Redux store. Handles user creation if the user is not found and dispatches actions
 * for error states.
 *
 * @param {string} email - The email address of the user to retrieve.
 * @param {AuthorisationProduct} product - An optional product to filter authorisations by.
 * @returns {Promise<UserRedux | void>} A Promise that resolves:
 *   - With the UserRedux object if the user is found.
 *   - With nothing (void) if the user is not found and needs to be created
 *     (creation logic not implemented yet).
 */

export const userGetUserByEmail = createAsyncThunk('user/getUserByEmail', async ({ email, product }: { email: string; product?: AuthorisationProduct }, { dispatch }) => {
  // Try and load the user based on the email and product.

  try {
    // -----------------------------------------------------------------------------------------------------------------------------------
    // The nonPortalProduct and portalProduct values are used by the Authorisations CRUD to seed the default product in the product
    // dropdown when creating a new record. Which one will be used is driven by the AuthorisationRole of the logged in user. If the role
    // is PORTAL_ADMIN then the portalProduct applies else it is the nonPortalProduct.
    // -----------------------------------------------------------------------------------------------------------------------------------

    // Before we do anything we'll set the nonPortalProduct.

    const nonPortalProductName = await getProductByNameAction(AuthorisationProduct.CLOUD_ADMIN);

    // Load the nonPortalProduct.

    dispatch(userLoadNonPortalProduct(nonPortalProductName.portalProduct ? nonPortalProductName.portalProduct : null));

    // And the portalProduct.

    const portalProductName = await getProductByNameAction(AuthorisationProduct.PORTAL_ADMIN);

    // Load the portalProduct.

    dispatch(userLoadPortalProduct(portalProductName.portalProduct ? portalProductName.portalProduct : null));

    // Try and get the user by the email and product if one is provided.

    const user = await getUserByEmailAction(email, product);

    // If we have a user we can update the user state and return.

    if (user) {
      // Load the user into user state.

      dispatch(userLoadUser(user));

      // Check to see whether we have only one authorisation and, if so, send it to
      // authorisation state.

      if (user.authorisations && user.authorisations.length === 1) {
        const authorisation = user.authorisations[0];
        dispatch(authorisationLoadAuthorisation(authorisation));
      }

      // And return doing nothing more.
      return;
    }

    // To be here we don't have a user so this must be a signup. We need to
    // create the user.

    const newUser = await addUserByEmailAction(email);

    // If we have a new user we can update state and return.

    if (newUser) {
      // Load the user into user state.

      dispatch(userLoadUser(newUser));

      // Clear the authorisations

      dispatch(authorisationLoadAuthorisation(null));
    }
  } catch (error) {
    // There was an error so display a toast.

    toast.error('Get User By Email: ' + formatErrorMessage(error));
  }
});
